import { Injectable, NgZone } from '@angular/core';
import {
  getMessaging,
  getToken,
  isSupported,
  Messaging,
  MessagePayload,
  onMessage,
} from 'firebase/messaging';
import { Subject } from 'rxjs';
import { deleteInstallations, getInstallations } from 'firebase/installations';
import { ToastrService } from 'ngx-toastr';
import { HttpHelpService } from 'src/app/Data/service/http-help.service';
import { environment } from 'src/environments/environment';
import { getFirebaseWebApp, initializeFirebaseWeb, prepareFirebaseWebStorage } from './firebase-core';

/** Must match `custom-sw.js` (FCM_SHOW_FROM_PAGE). */
const FCM_SW_SHOW_FROM_PAGE = 'FCM_SHOW_NOTIFICATION_FROM_PAGE';

function isFcmVerboseLogging(): boolean {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('fcmDebug') === '1') {
      return true;
    }
  } catch {
    /* ignore */
  }
  return !environment.production;
}

function fcmLog(...args: unknown[]): void {
  if (isFcmVerboseLogging()) {
    }
}

function fcmWarn(...args: unknown[]): void {
  console.warn('[FCM]', ...args);
}

/** URL-safe base64 public key for Web Push should decode to 65 bytes (0x04 + P-256 X,Y). */
function looksLikeWebPushVapidPublicKey(vapidKey: string): boolean {
  try {
    const padding = '='.repeat((4 - (vapidKey.length % 4)) % 4);
    const b64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(b64);
    return binary.length === 65 && binary.charCodeAt(0) === 0x04;
  } catch {
    return false;
  }
}

/**
 * Registers FCM web token with backend (`PUT /users/updateDevice`).
 * Uses the **same** service worker as `ServiceWorkerModule.register('custom-sw.js')`.
 */
export type FcmForegroundMessage = {
  title: string;
  body: string;
  url: string;
  rawData?: Record<string, string>;
};

@Injectable({ providedIn: 'root' })
export class FirebaseFcmService {
  /** Emits each time an FCM message arrives while the tab is in the foreground. */
  readonly foregroundMessage$ = new Subject<FcmForegroundMessage>();

  private messaging: Messaging | undefined;
  private foregroundListenerAttached = false;
  private registerInFlight: Promise<void> | null = null;
  private static swMessageLoggerAttached = false;

  constructor(
    private readonly http: HttpHelpService,
    private readonly toastr: ToastrService,
    private readonly ngZone: NgZone,
  ) {
    if (typeof window !== 'undefined' && isFcmVerboseLogging() && !FirebaseFcmService.swMessageLoggerAttached) {
      FirebaseFcmService.swMessageLoggerAttached = true;
      navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        fcmLog('SW→page message event (raw):', event?.data);
      });
    }
  }

  /**
   * Foreground tabs receive FCM via `onMessage` only — `custom-sw.js` `onBackgroundMessage`
   * does not run while the app tab is focused. Call once after Firebase init (e.g. from AppComponent).
   */
  ensureForegroundListener(): void {
    void this.attachForegroundListenerIfNeeded();
  }

  private async attachForegroundListenerIfNeeded(): Promise<void> {
    if (typeof window === 'undefined') {
      fcmLog('attachForegroundListener: skipped (no window)');
      return;
    }
    if (this.foregroundListenerAttached) {
      fcmLog('attachForegroundListener: already attached');
      return;
    }
    if (!environment.firebaseWebPushVapidKey?.trim() || !environment.firebase?.apiKey) {
      fcmLog('attachForegroundListener: skipped (no VAPID or firebase config)');
      return;
    }
    try {
      const supported = await isSupported();
      if (!supported) {
        fcmWarn('attachForegroundListener: Firebase Messaging isSupported() === false');
        return;
      }
    } catch (e) {
      fcmWarn('attachForegroundListener: isSupported() threw:', e);
      return;
    }
    initializeFirebaseWeb();
    const app = getFirebaseWebApp();
    if (!app) {
      fcmWarn('attachForegroundListener: Firebase app missing after initializeFirebaseWeb()');
      return;
    }
    let messaging: Messaging;
    try {
      messaging = getMessaging(app);
    } catch (e) {
      fcmWarn('attachForegroundListener: getMessaging failed:', e);
      return;
    }
    this.messaging = messaging;
    onMessage(messaging, (payload: MessagePayload) => {
      fcmLog('onMessage (foreground) payload:', JSON.stringify(payload));
      void this.showForegroundNotification(payload);
    });
    this.foregroundListenerAttached = true;
    fcmLog(
      'foreground listener attached; visibility:',
      document.visibilityState,
      'focused:',
      document.hasFocus(),
      'Notification.permission:',
      typeof Notification !== 'undefined' ? Notification.permission : 'n/a',
    );
  }

  private async showForegroundNotification(payload: MessagePayload): Promise<void> {
    if (typeof localStorage !== 'undefined' && !localStorage.getItem('token')) {
      fcmLog('showForegroundNotification skipped: user signed out');
      return;
    }

    const title =
      payload.notification?.title ||
      (payload.data && (payload.data['title'] as string)) ||
      'إشعار';
    const body =
      payload.notification?.body ||
      (payload.data && ((payload.data['body'] as string) || (payload.data['message'] as string))) ||
      '';
    const url =
      (payload.data && (payload.data['url'] as string)) ||
      payload.fcmOptions?.link ||
      '/';
    const rawData: Record<string, string> = {};
    if (payload.data) {
      for (const [k, v] of Object.entries(payload.data)) {
        if (v != null) {
          rawData[k] = String(v);
        }
      }
    }
    fcmLog('showForegroundNotification resolved:', { title, body, url, rawData });

    this.foregroundMessage$.next({ title, body, url, rawData });

    /* In-app toast only while the tab is focused — skip native OS banner to avoid duplicate clicks / markAsRead calls. */
    this.ngZone.run(() => {
      try {
        this.toastr.info(body || '', title, {
          timeOut: 12000,
          closeButton: true,
          tapToDismiss: true,
          enableHtml: false,
        });
        fcmLog('toastr.info shown for foreground FCM');
      } catch (e) {
        fcmWarn('toastr.info failed:', e);
      }
    });
  }

  /**
   * Obtain an FCM token when browser permission allows it.
   * @param requestPermission When true, calls Notification.requestPermission() if still "default".
   */
  async obtainFcmToken(requestPermission = false): Promise<string | null> {
    const vapidKey = environment.firebaseWebPushVapidKey?.trim();
    if (!vapidKey || !environment.firebase?.apiKey) {
      return null;
    }
    if (!looksLikeWebPushVapidPublicKey(vapidKey)) {
      console.warn(
        '[FCM] firebaseWebPushVapidKey does not look like a Firebase Web Push certificate ' +
          '(Console → Project settings → Cloud Messaging → Web Push certificates). ' +
          'Wrong keys cause "push service error".'
      );
    }
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return null;
    }

    try {
      const supported = await isSupported();
      if (!supported) {
        console.warn('[FCM] Firebase Messaging not supported in this browser/context');
        return null;
      }
    } catch (e) {
      console.warn('[FCM] isSupported() failed:', e);
      return null;
    }

    let permission = Notification.permission;
    if (permission === 'default' && requestPermission) {
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') {
      fcmLog('obtainFcmToken skipped: Notification.permission =', Notification.permission);
      return null;
    }

    await prepareFirebaseWebStorage();
    initializeFirebaseWeb();
    const app = getFirebaseWebApp();
    if (!app) {
      console.warn('[FCM] Firebase app not initialized');
      return null;
    }

    try {
      this.messaging = getMessaging(app);
    } catch (e) {
      console.warn('[FCM] getMessaging failed:', e);
      return null;
    }
    this.ensureForegroundListener();

    const maxAttempts = 15;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        await new Promise((r) => setTimeout(r, attempt === 0 ? 600 : 900));
        const registration = await navigator.serviceWorker.ready;
        if (!registration?.active) {
          console.warn(`[FCM] attempt ${attempt + 1}: service worker not active yet`);
          continue;
        }
        const existingPush = await registration.pushManager.getSubscription();
        if (existingPush) {
          await existingPush.unsubscribe();
          }
        if (attempt === 0) {
          try {
            await deleteInstallations(getInstallations(app));
            fcmLog('cleared Firebase Installations before getToken');
          } catch (installErr) {
            fcmWarn('deleteInstallations skipped/failed (may be first run):', installErr);
          }
        }
        const token = await getToken(this.messaging!, {
          vapidKey,
          serviceWorkerRegistration: registration,
        });
        if (!token) {
          console.warn(`[FCM] attempt ${attempt + 1}: getToken returned empty`);
          continue;
        }
        fcmLog(
          'getToken OK (compare with backend logs); token suffix:',
          token.slice(-16),
          'length:',
          token.length,
        );
        return token;
      } catch (e) {
        console.warn(`[FCM] attempt ${attempt + 1}/${maxAttempts} failed:`, e);
      }
    }
    console.warn('[FCM] gave up after retries — check service worker registration for custom-sw.js');
    return null;
  }

  /**
   * @deprecated Use PushDeviceService.syncTokenIfEnabled() after login instead.
   */
  async tryRegisterAfterLogin(): Promise<void> {
    if (this.registerInFlight) {
      return this.registerInFlight;
    }
    this.registerInFlight = this.registerDeviceTokenInternal();
    try {
      await this.registerInFlight;
    } finally {
      this.registerInFlight = null;
    }
  }

  private async registerDeviceTokenInternal(): Promise<void> {
    const token = await this.obtainFcmToken(false);
    if (!token) {
      return;
    }
    this.http.updateUserDeviceToken({ device_type: 'web', device_token: token }).subscribe({
      next: () => {
        this.ensureForegroundListener();
      },
      error: (err) =>
        console.warn('[FCM] PUT /users/updateDevice failed:', err?.error ?? err?.message ?? err),
    });
  }
}
